import { useEffect } from 'react';

import type Handsontable from 'handsontable';

/**
 * Shared hook for handling keyboard events in editor components
 * Reduces code duplication between AutocompleteEditor and DropdownEditor
 */
interface UseEditorKeyboardOptions {
  onFinished: () => void;
  /** Invoked with the native keydown event when Enter is pressed (after Escape handling). */
  onEnterKey?: (event: KeyboardEvent) => void;
  /** Invoked when ArrowDown is pressed while editor is opened. */
  onArrowDownKey?: (event: KeyboardEvent) => void;
  /** Invoked when ArrowUp is pressed while editor is opened. */
  onArrowUpKey?: (event: KeyboardEvent) => void;
  /**
   * When provided, Escape/Enter are only handled while Handsontable reports the editor is opened.
   * This listener uses the capture phase on `document`, which runs before Handsontable shortcuts
   * (bubble on documentElement); without this guard, Enter could be swallowed while the grid
   * still needs it to enter edit mode (e.g. empty プラント cell).
   */
  handsontable?: Handsontable.Core;
}

/**
 * Hook to handle Escape and Enter key events for editors
 */
export const useEditorKeyboard = ({
  onFinished,
  onEnterKey,
  onArrowDownKey,
  onArrowUpKey,
  handsontable,
}: UseEditorKeyboardOptions) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (handsontable) {
        const activeEditor = handsontable.getActiveEditor?.();
        if (!activeEditor?.isOpened?.()) {
          return;
        }
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        // Close without saving (keep current cell value)
        onFinished();
      } else if (event.key === 'Enter' && onEnterKey) {
        // Handle Enter key if handler is provided
        event.preventDefault();
        event.stopPropagation();
        onEnterKey(event);
      } else if (event.key === 'ArrowDown' && onArrowDownKey) {
        event.preventDefault();
        event.stopPropagation();
        onArrowDownKey(event);
      } else if (event.key === 'ArrowUp' && onArrowUpKey) {
        event.preventDefault();
        event.stopPropagation();
        onArrowUpKey(event);
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [onFinished, onEnterKey, onArrowDownKey, onArrowUpKey, handsontable]);
};
